import React, { useState, useEffect, useRef } from "react";
import { SearchIcon } from "../icons/inputIcons";
import {
  BagIcon,
  BuildingIcon,
  QuotationIcon,
  FollowUpIcon,
  ProductsIcon,
} from "../icons/otherIcons";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { clearResults, getGlobalData } from "../redux/slice/dashboardSlice";
import Input from "../ui/form/Input";
import StatusSegment from "../ui/tabs/StatusSegment";
import { Person } from "../ui/details-header/DetailsSkeleton";
import { formatCurrency } from "../utility/functions";

const PREVIEW = 4; // results per module under "All"; a module's own chip shows all it returned

// One entry per module: where its results come from, how a row reads and where it opens
// (tone = icon tile classes, color = the dot on its tab).
// Only fields the search API already sends; a missing one is simply left out of the row.
const MODULES = [
  {
    key: "leads",
    title: "Leads",
    Icon: BagIcon,
    tone: "bg-warning-subtle text-warning",
    color: "#c25e07",
    listPath: "/lead",
    path: (item) => `/lead/${item.id}`,
    name: (item) => item.lead_title,
    meta: (item) => [item.lead_number, item.lead_status?.name, item.customer?.company_name],
  },
  {
    key: "customers",
    title: "Customers",
    Icon: BuildingIcon,
    tone: "bg-primary-10 primary-text",
    color: "#213b73",
    listPath: "/customer",
    path: (item) => `/customer/${item.id}`,
    name: (item) => item.company_name || item.contact_person,
    meta: (item) => [item.company_name ? item.contact_person : null, item.mobile],
  },
  {
    key: "quotations",
    title: "Quotations",
    Icon: QuotationIcon,
    tone: "bg-info-subtle text-info",
    color: "#0891b2",
    listPath: "/quotation",
    path: (item) => `/quotation/${item.id}`,
    name: (item) => item.quotation_number,
    meta: (item) => [item.person?.person_name, item.lead?.lead_title],
  },
  {
    key: "followups",
    title: "Follow-ups",
    Icon: FollowUpIcon,
    tone: "bg-success-subtle text-success",
    color: "#0b8a4f",
    listPath: "/follow-ups",
    path: (item) => `/follow-ups/${item.id}`,
    name: (item) => item.title,
    meta: (item) => [
      item.followup_type?.name,
      item.related_to ? item.related_to.charAt(0).toUpperCase() + item.related_to.slice(1) : null,
    ],
  },
  {
    key: "products",
    title: "Products",
    Icon: ProductsIcon,
    tone: "bg-secondary-subtle text-secondary",
    color: "#667085",
    listPath: "/products",
    path: (item) => `/products/${item.id}`,
    name: (item) => item.product_name,
    meta: (item) => [
      item.product_code,
      item.unit_price !== undefined && item.unit_price !== null
        ? formatCurrency(item.unit_price)
        : null,
    ],
  },
];

// Small key shown in the footer hints
const Key = ({ children }) => (
  <span className="d-inline-flex px-2 border rounded-1 bg-white text-body fs-xxs lh-base">
    {children}
  </span>
);

const GlobalSearch = () => {
  const dispatch = useDispatch();
  const [query, setQuery] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [filter, setFilter] = useState("all"); // "all" or a module key
  const [activeIndex, setActiveIndex] = useState(-1); // row picked with the arrow keys
  const navigate = useNavigate();
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const { globalData, globalDataLoading } = useSelector((state) => state.dashboard);

  // Manual debounce
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      const trimmedQuery = query.trim();
      if (trimmedQuery.length > 1) {
        dispatch(getGlobalData({ search: trimmedQuery, limit: 10 }));
        setShowDropdown(true);
      } else {
        dispatch(clearResults());
        setShowDropdown(false);
      }
    }, 400);

    return () => clearTimeout(delayDebounceFn);
  }, [query, dispatch]);

  // New words: back to all modules, nothing picked
  useEffect(() => {
    setFilter("all");
    setActiveIndex(-1);
  }, [query]);

  const closeSearch = () => {
    setShowDropdown(false);
    setQuery("");
    dispatch(clearResults());
  };

  // Close dropdown component when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowDropdown(false);
        dispatch(clearResults());
        setQuery("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dispatch]);

  const searchResults = globalData?.search_results || {};
  const found = MODULES.map((m) => ({ ...m, items: searchResults[m.key] || [] })).filter(
    (m) => m.items.length > 0
  );
  const total = found.reduce((sum, m) => sum + m.items.length, 0);
  const hasResults = total > 0;

  // What is on screen, in order: a few per module under "All", everything under one module's chip
  const shown = found
    .filter((m) => filter === "all" || m.key === filter)
    .map((m) => ({ ...m, visible: filter === "all" ? m.items.slice(0, PREVIEW) : m.items }));
  const rows = shown.flatMap((m) => m.visible.map((item) => ({ module: m, item })));

  const handleNavigate = (path) => {
    navigate(path);
    closeSearch();
  };

  // Keep the picked row in view while moving with the arrows
  useEffect(() => {
    if (activeIndex < 0) return;
    listRef.current
      ?.querySelector(`[data-row="${activeIndex}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  const onKeyDown = (e) => {
    if (e.key === "Escape") {
      closeSearch();
      inputRef.current?.blur?.();
      return;
    }
    if (!showDropdown || !rows.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % rows.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (i <= 0 ? rows.length - 1 : i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const row = rows[activeIndex >= 0 ? activeIndex : 0];
      handleNavigate(row.module.path(row.item));
    }
  };

  let rowIndex = -1; // running row number across the modules, for the arrow keys

  return (
    <div className="position-relative" ref={wrapperRef}>
      {/* Search Input */}
      <div className="d-flex align-items-center bg-white">
        <Input
          label={{ name: "Search", id: "search", isHidden: true }}
          ref={inputRef}
          input={{
            type: "search",
            placeholder: "Search Customer, Leads, Follow-ups, Quotations, Products",
            value: query,
            onChange: (e) => setQuery(e.target.value),
            onKeyDown,
            className: "py-4",
          }}
          style={{ minWidth: "500px" }}
        />
      </div>

      {/* Dropdown Results */}
      {showDropdown && (hasResults || globalDataLoading || query.length > 1) && (
        <div
          className="position-absolute mt-2 bg-white rounded-4 shadow-lg border animate-fade-in-up top-100 z-1022 d-flex flex-column overflow-hidden"
          style={{ width: "500px", maxHeight: "calc(100vh - 100px)" }}
        >
          {globalDataLoading && !hasResults ? (
            // Searching: a few result-shaped rows, same shimmer as the rest of the CRM
            <div className="flex-column gap-7 p-8" aria-busy="true" aria-label="Searching">
              <Person />
              <Person name="70%" sub="35%" />
              <Person name="55%" sub="45%" />
            </div>
          ) : hasResults ? (
            <>
              {/* Show one module at a time: the same segmented tabs as the list pages (styles live under .list-v1) */}
              <div className="list-v1 px-6 py-5 border-bottom">
                <StatusSegment
                  ariaLabel="Show results from"
                  activeId={filter === "all" ? null : filter}
                  onSelect={(id) => {
                    setFilter(id ?? "all");
                    setActiveIndex(-1);
                  }}
                  items={[
                    { id: null, label: "All", count: total },
                    ...found.map((m) => ({
                      id: m.key,
                      label: m.title,
                      color: m.color,
                      count: m.items.length,
                    })),
                  ]}
                />
              </div>

              <div ref={listRef} className="overflow-y-auto custom-scroll p-3 flex-grow-1">
                {shown.map((m, mi) => (
                  <div key={m.key} className={mi > 0 ? "border-top mt-4 pt-4" : ""}>
                    <div className="flex-between px-4 py-2">
                      <span className="fs-xxs fw-semibold text-uppercase text-secondary">
                        {m.title} <span className="fw-normal opacity-75">· {m.items.length}</span>
                      </span>
                      <Link
                        to={`${m.listPath}?search=${encodeURIComponent(query.trim())}`}
                        className="primary-text fs-xs fw-medium text-decoration-none"
                        onClick={closeSearch}
                      >
                        View all {m.items.length} →
                      </Link>
                    </div>

                    {m.visible.map((item) => {
                      rowIndex += 1;
                      const index = rowIndex;
                      const isActive = index === activeIndex;
                      const meta = m.meta(item).filter(Boolean);
                      return (
                        <div
                          key={item.id}
                          data-row={index}
                          role="option"
                          aria-selected={isActive}
                          className={`d-flex align-items-center gap-5 px-4 py-4 rounded-3 cursor-pointer ${
                            isActive ? "bg-primary-10" : ""
                          }`}
                          onMouseEnter={() => setActiveIndex(index)}
                          onClick={() => handleNavigate(m.path(item))}
                        >
                          <span
                            className={`flex-center ratio-1 rounded-3 flex-shrink-0 ${m.tone}`}
                            style={{ width: "30px" }}
                          >
                            <m.Icon size={16} />
                          </span>
                          <span className="flex-column min-w-0 flex-grow-1">
                            <span className="fs-sm fw-medium text-primary text-truncate">
                              {m.name(item)}
                            </span>
                            {meta.length > 0 && (
                              <span className="fs-xs text-secondary text-truncate">
                                {meta.join(" · ")}
                              </span>
                            )}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>

              <div className="d-flex flex-wrap gap-8 px-6 py-3 border-top bg-light fs-xxs text-secondary">
                <span className="d-inline-flex align-items-center gap-2">
                  <Key>↑</Key>
                  <Key>↓</Key> move
                </span>
                <span className="d-inline-flex align-items-center gap-2">
                  <Key>Enter</Key> open
                </span>
                <span className="d-inline-flex align-items-center gap-2">
                  <Key>Esc</Key> close
                </span>
              </div>
            </>
          ) : (
            <div className="w-100 flex-center flex-column text-center" style={{ minHeight: "200px" }}>
              <div className="mb-3 text-secondary">
                <SearchIcon size={48} className="text-secondary opacity-25" />
              </div>

              <h6 className="fw-medium text-primary">No results found for "{query}"</h6>

              <p className="text-secondary fs-sm">Try searching with different keywords</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;
